import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import './creator-signup.css';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { ROUTES } from '../../../../shared/constants/routes';
import CreatorRegistration from './signupForm';
import { isUserLoggedIn } from '../../../../shared/utils/function';
import { clearAllUserState } from '../../../../shared/utils/authUtils';
import { getLocalItem } from '../../../../shared/utils/config/localStorage';
import { creatorRegistrationSchema } from '../../../../shared/validations/creatorRegistration';
import { reportError } from '../../../../shared/sentry';

/** Default profile tag for new creators (API category slug). Editable later in Settings → Profile tags. */
const CREATOR_SIGNUP_DEFAULT_TAGS = ['influencer'];

/**
 * CreatorSignup: registration form only (tags step skipped at signup to reduce friction).
 * Default tag is applied until the creator updates tags in settings.
 */
const CreatorSignup = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const isLoggedIn = isUserLoggedIn();
  const referralId = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('referral');
  }, []);

  useEffect(() => {
    const handleLogoutAndShowSignup = async () => {
      try {
        // Clear all user state using the reusable function
        await clearAllUserState(dispatch);
      } catch (error) {
        reportError(error, 'creatorSignup logout');
      }
    };

    if (referralId && isLoggedIn) {
      // Self-referral restriction: block creators from using their own referral link
      const currentUsername = getLocalItem('username');
      if (currentUsername && String(referralId).toLowerCase() === String(currentUsername).toLowerCase()) {
        toast.error('You cannot use your own referral link to sign up. Redirecting to signup without referral.');
        navigate.to(ROUTES.CREATOR_SIGNUP, { replace: true });
        return;
      }
      // If referralId exists and user is logged in, logout first then show signup form
      handleLogoutAndShowSignup();
    } else if (isLoggedIn && !referralId) {
      // If logged in without referralId, redirect to profile
      navigate.toProfile();
    }
  }, [isLoggedIn, navigate, referralId, dispatch]);

  // Form declaration using creatorRegistrationSchema
  const form = useForm({
    resolver: zodResolver(creatorRegistrationSchema),
    defaultValues: {
      accountType: 'monthly',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      dateOfBirth: '',
      phoneCountry: '+1',
      phoneCountryCode: 'US',
      phoneNumber: '',
      address: '',
      country: 'US',
      state: '',
      terms: false,
      documentType: 'id',
      profileImage: null,
      idFront: null,
      idBack: null,
      selectedTags: [...CREATOR_SIGNUP_DEFAULT_TAGS],
      referralId: referralId || undefined,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  const goToSignup = useCallback(() => {
    navigate.toSignup();
  }, [navigate]);

  return (
    <div className={`creator-signup-container`}>
      <CreatorRegistration
        form={form}
        onBack={goToSignup}
        referralId={referralId}
        showWelcomeTagsSummary={false}
      />
    </div>
  );
};

export default CreatorSignup;
